/**
 * BlueZoneLayoutEdit — Stand Planı Sayfası
 *
 * Artık manuel çizim yoktur; tüm plan Hall Management'ten gelir.
 * Sol alan  : Hall context SVG (StandHallView)
 * Sağ panel : Salt okunur stand bilgileri (Hall Management'ten)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Building2, Ruler, Layers, User, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import StandHallView from '../components/StandHallView';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* A/B/C/D → direction mapping */
const SIDE_LABELS = [
  { letter: 'A', dir: 'north',  color: '#ef4444' },
  { letter: 'B', dir: 'east',   color: '#f59e0b' },
  { letter: 'C', dir: 'south',  color: '#8b5cf6' },
  { letter: 'D', dir: 'west',   color: '#10b981' },
];

const DIRS_TR = { north: 'Kuzey', east: 'Doğu', south: 'Güney', west: 'Batı' };

const BlueZoneLayoutEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useLanguage();

  const [loading,         setLoading]         = useState(true);
  const [participant,     setParticipant]      = useState(null);
  const [hallViewData,    setHallViewData]     = useState(null);
  const [hallViewLoading, setHallViewLoading]  = useState(false);

  useEffect(() => {
    fetchAll();
  }, [id]);

  const fetchAll = async () => {
    const token = localStorage.getItem('cop31_ultra_token');
    setLoading(true);
    setHallViewLoading(true);

    try {
      const [listRes, hvRes] = await Promise.allSettled([
        apiClient.get(`/ultra-admin/blue-zone-layouts`),
        apiClient.get(`/ultra-admin/blue-zone-layouts/${id}/hall-view`),
      ]);

      if (listRes.status === 'fulfilled') {
        const p = listRes.value.data.find(x => x.id === id);
        if (p) setParticipant(p);
      }
      if (hvRes.status === 'fulfilled' && hvRes.value.data?.layout) {
        setHallViewData(hvRes.value.data.layout);
      }
    } catch (_) {
      // pass
    } finally {
      setLoading(false);
      setHallViewLoading(false);
    }
  };

  /* ── Computed values ────────────────────────────────────── */
  const layout     = hallViewData || {};
  const standNo    = layout.stand_no   || participant?.stand_no   || '—';
  const hallName   = layout.hall_name  || participant?.hall       || '—';
  const baseW      = layout.base_width  ?? participant?.layout?.base_width  ?? '—';
  const baseH      = layout.base_height ?? participant?.layout?.base_height ?? '—';
  const totalArea  = layout.total_area  ?? participant?.layout?.total_area  ?? '—';
  const openSides  = layout.open_sides  ?? participant?.layout?.open_sides  ?? [];
  const country    = layout.country    || participant?.country    || '—';
  const email      = participant?.email || '—';
  const fullName   = participant?.full_name || '—';
  const company    = participant?.company || null;
  const authorized = participant?.authorized_persons ?? [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <AdminSidebar language={language} activePage="Stand Layouts" />
        <div className="flex-1 flex items-center justify-center">
          <div className="h-10 w-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar language={language} activePage="Stand Layouts" />

      <div className="flex-1 overflow-auto">
        {/* ── Topbar ─────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm
                        flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ultra-admin/blue-zone')}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              {language === 'tr' ? 'Geri' : 'Back'}
            </button>
            <span className="text-gray-300">|</span>
            <div>
              <h1 className="text-lg font-bold text-gray-800">
                {language === 'tr' ? 'Stand Planı' : 'Stand Plan'}
              </h1>
              {email && email !== '—' && (
                <p className="text-xs text-gray-400">{email}</p>
              )}
            </div>
          </div>
          <LanguageToggle language={language} />
        </div>

        {/* ── Main Content ────────────────────────────────────── */}
        <div className="p-6">
          <div className="grid lg:grid-cols-3 gap-6">

            {/* ── Left: Hall View SVG ─────────────────────────── */}
            <div className="lg:col-span-2">
              <Card className="h-full shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-bold text-blue-800 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      {language === 'tr' ? 'Hall Planı' : 'Hall Floor Plan'}
                    </CardTitle>
                    {hallName !== '—' && (
                      <Badge variant="outline" className="text-xs text-gray-500 font-normal">
                        {hallName}
                      </Badge>
                    )}
                  </div>
                  {standNo !== '—' && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Stand <strong className="text-blue-700">{standNo}</strong>
                      {country !== '—' && <> · <strong>{country}</strong></>}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {hallViewLoading && (
                    <div className="flex items-center justify-center py-24">
                      <div className="h-8 w-8 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin" />
                    </div>
                  )}

                  {!hallViewLoading && !hallViewData && (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                      <Building2 className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-gray-400 font-medium text-sm">
                        {language === 'tr'
                          ? 'Bu katılımcıya henüz stand atanmamış'
                          : 'No stand assigned yet'}
                      </p>
                      <p className="text-gray-300 text-xs mt-1">
                        {language === 'tr'
                          ? 'Hall Management üzerinden stand atayın'
                          : 'Assign a stand from Hall Management'}
                      </p>
                    </div>
                  )}

                  {!hallViewLoading && hallViewData && (
                    <>
                      <StandHallView layout={hallViewData} maxH={520} />

                      {/* Legend */}
                      <div className="mt-3 flex flex-wrap gap-3 justify-center border-t pt-3 border-gray-100">
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="inline-block w-5 h-0.5 bg-green-500 rounded" />
                          {language === 'tr' ? 'Açık Kenar' : 'Open Side'}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="inline-block w-5 h-[3px] bg-gray-900 rounded" />
                          {language === 'tr' ? 'Kapalı Kenar' : 'Closed Side'}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="inline-block w-5 h-4 bg-gray-200 rounded-sm border border-gray-300" />
                          {language === 'tr' ? 'Komşu Stand' : 'Adjacent Stand'}
                        </span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Right: Info panels ──────────────────────────── */}
            <div className="flex flex-col gap-4">

              {/* Stand Bilgileri */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-500" />
                    {language === 'tr' ? 'Stand Bilgileri' : 'Stand Information'}
                  </CardTitle>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {language === 'tr'
                      ? 'Hall Management\'ten otomatik'
                      : 'Auto-synced from Hall Management'}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <InfoRow icon={<Layers className="h-3.5 w-3.5" />}
                    label={language === 'tr' ? 'Stand No' : 'Stand No'}
                    value={<span className="font-bold text-blue-700 text-base">{standNo}</span>} />
                  <InfoRow icon={<Building2 className="h-3.5 w-3.5" />}
                    label={language === 'tr' ? 'Salon' : 'Hall'}
                    value={hallName} />
                  <InfoRow icon={<Ruler className="h-3.5 w-3.5" />}
                    label={language === 'tr' ? 'Boyutlar' : 'Dimensions'}
                    value={baseW !== '—' ? `${baseW}m × ${baseH}m` : '—'} />
                  <InfoRow icon={<Layers className="h-3.5 w-3.5" />}
                    label={language === 'tr' ? 'Toplam Alan' : 'Total Area'}
                    value={totalArea !== '—' ? `${totalArea} m²` : '—'} />
                </CardContent>
              </Card>

              {/* Kenarlar */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-700">
                    {language === 'tr' ? 'Kenarlar' : 'Sides'}
                  </CardTitle>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {language === 'tr'
                      ? 'Hall Management\'ten otomatik belirlenir'
                      : 'Auto-determined from Hall Management'}
                  </p>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {SIDE_LABELS.map(({ letter, dir, color }) => {
                    const isOpen = openSides.includes(dir);
                    return (
                      <div key={letter}
                        className="flex items-center justify-between py-1.5 px-2
                                   rounded-lg bg-gray-50 border border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center
                                           w-6 h-6 rounded-full text-white text-xs font-bold"
                            style={{ background: color }}>
                            {letter}
                          </span>
                          <span className="text-xs text-gray-500">
                            {DIRS_TR[dir]}
                          </span>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold
                          ${isOpen
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : 'bg-gray-200 text-gray-600 border border-gray-300'}`}>
                          {isOpen
                            ? (language === 'tr' ? 'Açık' : 'Open')
                            : (language === 'tr' ? 'Kapalı' : 'Closed')}
                        </span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Katılımcı Bilgileri */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-500" />
                    {language === 'tr' ? 'Katılımcı' : 'Participant'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <InfoRow icon={<User className="h-3.5 w-3.5" />}
                    label={language === 'tr' ? 'Ad Soyad' : 'Full Name'}
                    value={<span className="font-semibold text-gray-800">{fullName}</span>} />
                  {company && (
                    <InfoRow icon={<Building2 className="h-3.5 w-3.5" />}
                      label={language === 'tr' ? 'Firma' : 'Company'}
                      value={company} />
                  )}
                  <InfoRow icon={<Mail className="h-3.5 w-3.5" />}
                    label="E-posta"
                    value={<span className="text-xs break-all">{email}</span>} />
                  <InfoRow icon={<span className="text-sm">🌍</span>}
                    label={language === 'tr' ? 'Ülke' : 'Country'}
                    value={country} />
                  {authorized.length > 0 && (
                    <div className="pt-1">
                      <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {language === 'tr' ? 'Yetkili Kişiler' : 'Authorized Persons'}
                      </p>
                      {authorized.map((ap, i) => (
                        <div key={i} className="flex items-center gap-2 py-1 px-2
                             bg-gray-50 rounded-lg border border-gray-100 mb-1">
                          <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center
                               justify-center text-blue-700 text-xs font-bold flex-shrink-0">
                            {(ap.name || ap.full_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700">
                              {ap.name || ap.full_name || '—'}
                            </p>
                            {ap.title && (
                              <p className="text-[10px] text-gray-400">{ap.title}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Tiny helper ── */
function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 leading-none mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-700 truncate">{value}</p>
      </div>
    </div>
  );
}

export default BlueZoneLayoutEdit;
